import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import type {
  CreateOrganisationRequest,
  Organisation,
  OrganisationId,
  UpdateOrganisationRequest,
} from "@thalamos/vision-common";
import {
  CreateOrganisationSchema,
  OrganisationArraySchema,
  OrganisationIdSchema,
  OrganisationRoleSchema,
  tk,
  UpdateOrganisationSchema,
} from "@thalamos/vision-common";

import api, { apiHooks } from "../../../../api/index.js";
import { useToastNotifications } from "../../../../hooks/useToastNotifications.js";
import { createRoute } from "../../../../routing/createRoute.js";
import { Banner } from "../../../Banner/Banner.js";
import { PopupDialog } from "../../../Dialog/PopupDialog.js";

function OrganisationPropertiesCreateEdit({
  organisation,
  organisations,
  organisationId,
  refetchOrgs,
}: {
  organisation: Organisation | undefined;
  organisations: Organisation[];
  organisationId: OrganisationId;
  refetchOrgs: ReturnType<typeof apiHooks.organisation.search>[1];
}) {
  const notifications = useToastNotifications();
  const [submitting, setSubmitting] = useState(false);

  const { t } = useTranslation();

  const [updatedOrganisation, setUpdatedOrganisation] =
    useState<UpdateOrganisationRequest>({
      status: "active",
      name: "",
      address: "",
      postalCode: "",
      managingOrganisationId: null,
      role: OrganisationRoleSchema.enum.PoliceForce,
    });

  useEffect(() => {
    if (organisation) {
      const { id, ...rest } = organisation;
      setUpdatedOrganisation(rest);
    }
  }, [organisation]);

  const isValid = organisation
    ? UpdateOrganisationSchema.safeParse(updatedOrganisation).success
    : CreateOrganisationSchema.safeParse(updatedOrganisation).success;

  return (
    <>
      <TextField
        label="Name"
        value={updatedOrganisation.name ?? ""}
        onChange={(e) => {
          setUpdatedOrganisation({
            ...updatedOrganisation,
            name: e.target.value,
          });
        }}
      />
      <TextField
        fullWidth
        label="Address"
        value={updatedOrganisation.address ?? ""}
        onChange={(e) => {
          setUpdatedOrganisation({
            ...updatedOrganisation,
            address: e.target.value,
          });
        }}
      />
      <TextField
        fullWidth
        label="Postal Code"
        value={updatedOrganisation.postalCode ?? ""}
        onChange={(e) => {
          setUpdatedOrganisation({
            ...updatedOrganisation,
            postalCode: e.target.value,
          });
        }}
      />
      <FormControl fullWidth>
        <InputLabel id="demo-simple-select-label">Role</InputLabel>
        <Select
          labelId="demo-simple-select-label"
          id="demo-simple-select"
          value={
            updatedOrganisation.role ?? OrganisationRoleSchema.enum.PoliceForce
          }
          label="Role"
          onChange={(value) => {
            if (value.target.value) {
              setUpdatedOrganisation({
                ...updatedOrganisation,
                role: value.target.value,
              });
            }
          }}
        >
          <MenuItem value={OrganisationRoleSchema.enum.Admin}>Admin</MenuItem>
          <MenuItem value={OrganisationRoleSchema.enum.PoliceForce}>
            Police Force
          </MenuItem>
          <MenuItem value={OrganisationRoleSchema.enum.PoliceNationalUnit}>
            Police National Unit
          </MenuItem>
          <MenuItem value={OrganisationRoleSchema.enum.PoliceRegionalUnit}>
            Police Regional Unit
          </MenuItem>
          <MenuItem value={OrganisationRoleSchema.enum.PoliceGeographicUnit}>
            Police Geographic Unit
          </MenuItem>
          <MenuItem value={OrganisationRoleSchema.enum.PoliceCommandUnit}>
            Police Command Unit
          </MenuItem>
        </Select>
      </FormControl>

      <FormControl fullWidth>
        <InputLabel id="demo-simple-select-label">
          Managing Organisation
        </InputLabel>
        <Select
          labelId="demo-simple-select-label"
          id="demo-simple-select"
          value={updatedOrganisation.managingOrganisationId ?? "null"}
          label="Role"
          onChange={(value) => {
            if (value.target.value) {
              setUpdatedOrganisation({
                ...updatedOrganisation,
                managingOrganisationId:
                  value.target.value === "null" ? null : value.target.value,
              });
            }
          }}
        >
          <MenuItem value={"null"}>None (Root Org)</MenuItem>
          {...organisations
            .filter((o) => o.id !== organisation?.id)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((org) => (
              <MenuItem key={org.id} value={org.id}>
                {org.name}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      {updatedOrganisation.role ===
        OrganisationRoleSchema.enum.PoliceCommandUnit && (
        <FormControlLabel
          control={
            <Checkbox
              checked={updatedOrganisation.excludeAsIncidentLocation ?? false}
              onChange={(e) => {
                setUpdatedOrganisation({
                  ...updatedOrganisation,
                  excludeAsIncidentLocation: e.target.checked,
                });
              }}
            />
          }
          label={t("admin.organisations.excludeAsIncidentLocation")}
        />
      )}

      <Button
        disabled={!isValid || submitting}
        variant="contained"
        onClick={() => {
          setSubmitting(true);
          if (!organisation) {
            api.organisation
              .create(
                organisationId,
                (({ status, ...rest }) => rest)(
                  updatedOrganisation,
                ) as CreateOrganisationRequest,
              )
              .then(async (response) => {
                if (response.status === 204) {
                  notifications.show("Organisation created successfully", {
                    severity: "success",
                  });
                  await refetchOrgs();
                } else {
                  notifications.show("Failed to create organisation", {
                    severity: "error",
                  });
                }
              })
              .catch((_error: unknown) => {
                notifications.show("Failed to create organisation", {
                  severity: "error",
                });
              });
            setSubmitting(false);
          } else {
            api.organisation
              .update(organisation.id, updatedOrganisation)
              .then(async (response) => {
                if (response.status === 204) {
                  notifications.show("Organisation updated successfully", {
                    severity: "success",
                  });
                  await refetchOrgs();
                } else {
                  notifications.show("Failed to update organisation", {
                    severity: "error",
                  });
                }
              })
              .catch((_error: unknown) => {
                notifications.show("Failed to update organisation", {
                  severity: "error",
                });
              })
              .finally(() => {
                setSubmitting(false);
              });
          }
          return;
        }}
      >
        {organisation
          ? t("admin.organisations.update")
          : t("admin.organisations.create")}
      </Button>
    </>
  );
}

function OrganisationManagementDeleteModal({
  open,
  onClose,
  onDelete,
}: {
  open: boolean;
  onClose: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();

  return (
    <PopupDialog open={open} onClose={onClose}>
      <DialogTitle>{t("admin.organisations.deleteModal.title")}</DialogTitle>
      <DialogContent>
        <Banner
          bannerType="warning"
          body={tk("admin.organisations.deleteModal.message")}
        />
      </DialogContent>
      <DialogActions sx={{ display: "flex", justifyContent: "space-between" }}>
        <Button onClick={onClose} color="primary">
          {t("admin.organisations.deleteModal.cancel")}
        </Button>
        <Button onClick={onDelete} color="error">
          {t("admin.organisations.deleteModal.confirm")}
        </Button>
      </DialogActions>
    </PopupDialog>
  );
}

type OrganisationManagementInnerProps = {
  organisations: Organisation[];
  organisation: Organisation | undefined;
  organisationId: OrganisationId;
  refetchOrgs: ReturnType<typeof apiHooks.organisation.search>[1];
};

function OrganisationManagementInner({
  organisations,
  organisation,
  organisationId,
  refetchOrgs,
}: OrganisationManagementInnerProps) {
  const navigate = useNavigate();

  const notifications = useToastNotifications();

  const isCreate = !organisation;

  const { t } = useTranslation();

  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const canDelete =
    organisations.filter((org) => org.managingOrganisationId === organisationId)
      .length === 0;

  return (
    <Box
      sx={{
        display: "flex",
        gap: "2rem",
        flexDirection: "column",
        maxWidth: "600px",
        padding: "2rem",
      }}
    >
      <OrganisationManagementDeleteModal
        open={openDeleteModal}
        onClose={() => {
          setOpenDeleteModal(false);
        }}
        onDelete={() => {
          api.organisation
            .delete(organisationId)
            .then((response) => {
              if (response.status === 204) {
                notifications.show("Organisation deleted successfully", {
                  severity: "success",
                });
                navigate(createRoute.home());
              } else {
                notifications.show("Failed to delete organisation", {
                  severity: "error",
                });
              }
            })
            .catch(() => {
              notifications.show("Failed to delete organisation", {
                severity: "error",
              });
            });
        }}
      />
      <Typography variant="h4" component="div">
        {isCreate
          ? t("admin.organisations.createTitle")
          : t("admin.organisations.updateTitle")}
      </Typography>
      <OrganisationPropertiesCreateEdit
        organisation={organisation}
        organisations={organisations}
        organisationId={organisationId}
        refetchOrgs={refetchOrgs}
      />
      {!isCreate && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "2em",
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              navigate(
                createRoute.adminOrganisationConfigManagement(organisation.id),
              );
            }}
          >
            {t("admin.organisations.updateConfig")}
          </Button>
          {canDelete && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                setOpenDeleteModal(true);
              }}
            >
              {t("admin.organisations.delete")}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}

export function OrganisationManagement() {
  const [{ data, loading }, refetchOrgs] = apiHooks.organisation.search(
    "",
    1000,
  );
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [organisation, setOrganisation] = useState<Organisation | undefined>(
    undefined,
  );

  const params = useParams();

  const organisationId = OrganisationIdSchema.safeParse(params.organisationId);

  if (!organisationId.success) {
    throw new Error("Invalid organisation ID");
  }

  useEffect(() => {
    setOrganisation(
      organisations.find((org) => org.id === organisationId.data),
    );
  }, [organisations, organisationId.data]);

  useEffect(() => {
    if (data) {
      const orgs = OrganisationArraySchema.parse(data.results);
      setOrganisations(orgs);
    }
  }, [data]);

  return loading || organisations.length === 0 ? (
    <CircularProgress />
  ) : (
    <OrganisationManagementInner
      organisations={organisations}
      organisation={organisation}
      organisationId={organisationId.data}
      refetchOrgs={refetchOrgs}
    />
  );
}
